import React from 'react'

import { Stack } from '@mantine/core'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useMst } from '#state'
import { BetaFlag, WorkspaceDetailsPayload } from '#util/ScriptoApiClient/types'

import { ToggleSwitchForm } from './ToggleSwitchForm'

interface BetaFlagToggleProps {
  flag: BetaFlag
  workspace: WorkspaceDetailsPayload
}

const BetaFlagToggle = (props: BetaFlagToggleProps) => {
  const { apiClient } = useMst()
  const queryClient = useQueryClient()

  const checked = Boolean(
    props.workspace.betaFlags.some(
      (enabledFlag) => enabledFlag === props.flag.name,
    ),
  )

  const mutation = useMutation({
    async mutationFn(enable: boolean) {
      const { enabled } = await apiClient.toggleBetaFlagByStaff({
        orgId: props.workspace.id,
        flagId: props.flag.id,
        enable,
      })

      return enabled
    },

    onSuccess(enabled) {
      queryClient.setQueryData(
        ['staff_org_info', props.workspace.id],
        (oldWorkspace: WorkspaceDetailsPayload) => {
          const currentFlags = new Set(oldWorkspace.betaFlags)

          if (enabled) {
            currentFlags.add(props.flag.name)
          } else {
            currentFlags.delete(props.flag.name)
          }

          return {
            ...oldWorkspace,
            betaFlags: Array.from(currentFlags),
          }
        },
      )
    },
  })

  return (
    <ToggleSwitchForm
      label={props.flag.name}
      description={props.flag.description}
      checked={checked}
      mutation={mutation}
      confirmDisableMessage={`Are you sure you want to disable "${props.flag.name}" for this workspace?`}
    />
  )
}

export function BetaFlagsForm({
  org,
  betaFlags,
}: {
  org: WorkspaceDetailsPayload
  betaFlags: BetaFlag[]
}) {
  return (
    <Stack>
      {betaFlags
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((f) => (
          <BetaFlagToggle key={f.id} flag={f} workspace={org} />
        ))}
    </Stack>
  )
}
